import { LanguageService } from "../common/language.service";
import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {  Observable, combineLatest} from "rxjs";
import {  map, catchError } from "rxjs/operators";

import { CommonService } from "../common/common.service";
import { EventBusService, EventsEnum } from "../common/event-bus.service";
import { LocalStorageService } from "../common/local-storage.service";
import { LoggerService } from "../common/logger.service";
import { ConfigService } from "../services/config.service";
import { CsrService } from "../services/csr.service";
import { ChargeFrequencyEnum } from "../domain/enums";
import { ICampaignBindingModel, IHospitalBindingModel } from "../domain/types";
import { DataService } from "../services/data.service";

interface ISplashRouteParams {
  campaignId?: string | null;
  hospitalId?: string | null;
  radioStationId?: string | null;
  yes?: string | null;
  amount?: string | null;
  recurring?: string | null;
}

@Component({
    templateUrl: "./home.component.html",
    styleUrls: ["./home.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HomeComponent implements OnInit {
  showPage = false;
  private _enableDonation = true;
  imageUrl = "";
  hideImage = false;
  private _routeParams: ISplashRouteParams = {
    campaignId: null,
    hospitalId: null,
    radioStationId: null,
    yes: null,
    amount: null,
    recurring: null,
  };
  showAnimation: boolean = false;

  constructor(
    private _languageService: LanguageService,
    private _commonService: CommonService,
    private _route: ActivatedRoute,
    private _events: EventBusService,
    private _csrService: CsrService,
    private _localStorageService: LocalStorageService,
    private _router: Router,
    private _dataService: DataService,
    private _configService: ConfigService,
    private _loggerService: LoggerService,
  ) {
    this._route.data.subscribe((data) => {
      this._events.emit({ name: EventsEnum.RouteChanged, data });
    });

    this._commonService.pageTitle = "";
    this._commonService.pageSubTitle = "";
    this._commonService.shuffelAbout.subscribe(() => {
      this.showAnimation = true;
    });
    this._commonService.shuffelContact.subscribe(() => {
      this.showAnimation = true;
    });
  }

  ngOnInit(): void {

    this._dataService.isCampaignLegacyUrlEnabled().subscribe({
      next: (allowLegacy: boolean) => {
        this._csrService.allowLegacyUrlFormat = allowLegacy;
        this._processRouteParameters();
      },
      error: () => {
        this._commonService.toast.error(this._languageService.translate("ERROR_LOAD_DONATION_FORM"));
        // Default to false (only hashed IDs) if API call fails
        this._csrService.allowLegacyUrlFormat = false;
        this._processRouteParameters();
      }
    });
  }

  donate() {
    this._router.navigate(["/donate"], { skipLocationChange: true });
  }

  get enableDonation(): boolean {
    return this._enableDonation;
  }

  set enableDonation(value: boolean) {
    this._enableDonation = value;
    this._csrService.donationEnabled.set(value);
  }

  toggleImage(shown) {
    this.hideImage = !shown;
  }

  private _processRouteParameters(): void {
    this._route.paramMap.subscribe((paramsMap) => {
      this._routeParams.campaignId = paramsMap.get("cid");
      this._routeParams.hospitalId = paramsMap.get("hid");
      this._routeParams.radioStationId = paramsMap.get("rid");
      this._routeParams.yes = paramsMap.get("yes");

      this._commonService.activateController([], "home").then(() => {
        this._csrService.initialize();

        if (
          this._routeParams.hospitalId ||
          this._routeParams.campaignId ||
          this._routeParams.radioStationId
        ) {
          try {
            if (this._routeParams.campaignId) {
              const processedCampaignId = this._csrService.validateId(this._routeParams.campaignId, "campaign ID");
              this._csrService.setCampaignId(processedCampaignId);
              this._localStorageService.set("lastCampaignId", this._csrService.campaignId());
            }

            if (this._routeParams.hospitalId && this._routeParams.hospitalId !== "0") {
              const processedHospitalId = this._csrService.validateId(this._routeParams.hospitalId, "hospital ID");
              this._csrService.setHospitalId(processedHospitalId);
              this._localStorageService.set("lastHospitalId", this._csrService.hospitalId());
            } else {
              this._csrService.setHospitalId("0");
              this._localStorageService.set("lastHospitalId", "0");
            }

            if (this._routeParams.radioStationId) {
              const processedRadioStationId = this._csrService.validateId(this._routeParams.radioStationId, "radio station ID");
              this._csrService.radioStationId.set(processedRadioStationId);
              this._localStorageService.set("lastRadioStationId", this._csrService.radioStationId());
            } else {
              this._csrService.radioStationId.set("0");
              this._localStorageService.set("lastRadioStationId", "0");
            }

            this._getHospitalCampaignRadioStation();
          } catch (e) {
            this.enableDonation = false;
            console.error(e);
            this._commonService.toast.error(this._languageService.translate("ERROR_INVALID_DONATION_LINK"));
          }
        } else {
          if (this._csrService.campaignId() === "") {
            this.enableDonation = false;
          } else {
            this._getHospitalCampaignRadioStation();
          }
        }
      });
    });
  }

  private _getHospitalCampaignRadioStation(): void {
    let requests: Observable<any>[] = [this._getCampaignForCsr()];

    if (this._csrService.hospitalId() && this._csrService.hospitalId() !== "0") {
      requests.push(this._getHospital());
    }
    if (this._csrService.radioStationId() && this._csrService.radioStationId() !== "0") {
      requests.push(this._getRadioStation());
    }

    this._events.emit({ name: EventsEnum.SpinnerToggle, data: true });

    combineLatest(requests).subscribe({
      next: () => {
        this._events.emit({ name: EventsEnum.SpinnerToggle, data: false });

        if (this._routeParams.yes === "y") {
          if (this._routeParams.amount) {
            this._csrService.donationAmount = Number(this._routeParams.amount);
          }

          if (this._routeParams.recurring === "y") {
            this._csrService.chargeFrequency = ChargeFrequencyEnum.Monthly;
          }

          this._router.navigate(["/getdonor"], { skipLocationChange: true });
        } else {
          this.showPage = true;
        }
      },
      error: () => {
        this._events.emit({ name: EventsEnum.SpinnerToggle, data: false });
      }
    });
  }

  private _getHospital(): Observable<IHospitalBindingModel> {
    return this._dataService.getHospital(this._csrService.hospitalId()).pipe(
      map((hospital) => {
        this._csrService.hospital.set(hospital);
        this._csrService.setHospitalName(hospital.hospitalName);
        return hospital;
      }),
      catchError((error) => {
        const htmlMessage = this._commonService.jsonMessage(error);

        this._loggerService.logError({
          message: `
                  Could not get the Hospital
                  <br />
                  ${htmlMessage}
                `,
        });

        this.enableDonation = false;

        throw error;
      })
    );
  }

  private _getCampaignForCsr(): Observable<ICampaignBindingModel> {
    const campaignId = this._csrService.campaignId();
    return this._dataService.getCampaignForCsr(campaignId).pipe(
      map((campaign) => {
        this._events.emit({
          name: EventsEnum.LanguageChanged,
          data: campaign.languageValue,
        });

        this.toggleImage(true);

        this.imageUrl = this._commonService.serviceUrl(this._configService.apiServices.campaign) + "SplashImage/" + campaignId;
        if (this.imageUrl) {
          this._csrService.campaignImageUrl.set(this.imageUrl);
        }
        
        this._csrService.campaign.set(campaign);
        return campaign;
      }),
      catchError((error) => {
        const htmlMessage = this._commonService.jsonMessage(error);
        this._loggerService.logError({
          message: `
                  Could not get the Campaign
                  <br />
                  ${htmlMessage}
                `,
        });

        this.enableDonation = false;
        throw error;
      })
    );
  }

  private _getRadioStation() {
    return this._dataService
      .getRadioStation(this._csrService.radioStationId())
      .pipe(
        map((radioStation) => {
          this._csrService.radioStation.set(radioStation);
          return radioStation;
        }),
        catchError((error) => {
          const htmlMessage = this._commonService.jsonMessage(error);
          this._loggerService.logError({
            message: `
                    Could not get the Radio Station
                    <br />
                    ${htmlMessage}
                  `,
          });
          this.enableDonation = false;
          throw error;
        })
      );
  }
}
